import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, output, Signal, untracked } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { TranslatePipe } from '@ngx-translate/core';
import { CRON_FREQUENCY_OPTIONS, CRON_MONTH_DAYS, CRON_WEEK_DAYS, CronConfiguratorControlNames, CronFrequency } from './cron.model';
import { buildCron, dateToTimeString, DEFAULT_CRON_SCHEDULE, parseCron, timeStringToDate } from './cron.utils';

@Component({
  selector: 'iot-platform-ui-cron-configurator',
  standalone: true,
  imports: [ReactiveFormsModule, TranslatePipe, MatButtonToggleModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatTimepickerModule],
  providers: [provideNativeDateAdapter(), { provide: MAT_DATE_LOCALE, useValue: 'en-GB' }],
  templateUrl: './cron-configurator.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CronConfiguratorComponent {
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  cron = input<string | null>(null);
  cronChange = output<string | null>();

  protected readonly controlNames = CronConfiguratorControlNames;
  protected readonly CronFrequency = CronFrequency;
  protected readonly frequencyOptions = CRON_FREQUENCY_OPTIONS;
  protected readonly monthDays = CRON_MONTH_DAYS;
  protected readonly weekDays = CRON_WEEK_DAYS;

  form: FormGroup = new FormGroup({});
  formChanges: Signal<unknown> = toSignal(this.form.events.pipe(takeUntilDestroyed(this.destroyRef)));

  frequency: Signal<CronFrequency> = computed(() => {
    this.formChanges();
    return this.form.get(CronConfiguratorControlNames.FREQUENCY)?.value ?? CronFrequency.DAILY;
  });

  initFormEffect = effect(() => {
    this.cron();
    untracked(() => this.initForm());
  });

  emitCronEffect = effect(() => {
    this.formChanges();
    const raw = this.form.getRawValue();
    this.cronChange.emit(
      buildCron({
        frequency: raw[CronConfiguratorControlNames.FREQUENCY],
        time: dateToTimeString(raw[CronConfiguratorControlNames.TIME] as Date),
        weekDay: raw[CronConfiguratorControlNames.WEEK_DAY],
        monthDay: raw[CronConfiguratorControlNames.MONTH_DAY]
      })
    );
  });

  private initForm(): void {
    const schedule = parseCron(this.cron()) ?? DEFAULT_CRON_SCHEDULE;
    this.form.addControl(CronConfiguratorControlNames.FREQUENCY, new FormControl<CronFrequency>(schedule.frequency, { nonNullable: true }));
    this.form.addControl(CronConfiguratorControlNames.TIME, new FormControl<Date>(timeStringToDate(schedule.time), { nonNullable: true }));
    this.form.addControl(CronConfiguratorControlNames.WEEK_DAY, new FormControl<number>(schedule.weekDay, { nonNullable: true }));
    this.form.addControl(CronConfiguratorControlNames.MONTH_DAY, new FormControl<number>(schedule.monthDay, { nonNullable: true }));
  }
}
